/**
 * DatePicker — rich shadcn Popover + Calendar for reservation flows.
 *
 * Replaces the native <input type="date"> which looked cramped and
 * inconsistent across browsers (mm/dd/yyyy on some, no Turkish month
 * names, etc.). The Popover trigger shows the picked date in Turkish
 * long format; the Calendar itself uses react-day-picker under the hood
 * with our brand accent colors.
 *
 * Contract:
 *   • Parent passes ISO string ("2026-08-25") via `value` and receives
 *     the same shape back via `onChange`.
 *   • Empty value → placeholder text; user can clear via the "Temizle"
 *     button inside the popover.
 */
import React, { useState } from "react";
import { format, parseISO, isValid, addYears, subYears } from "date-fns";
import { tr } from "date-fns/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// 3-letter Turkish weekday abbreviations for the picker header.
// react-day-picker's default from date-fns/tr is 2 letters (Pt/Sa/…) —
// the customer feedback asked for the fuller Pzt/Sal/Çar style.
const TR_SHORT_DAYS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

// Custom locale wrapper: keep tr defaults but override the short weekday
// label so the calendar shows Pzt/Sal/Çar instead of Pt/Sa/Ça.
const trShort = {
  ...tr,
  localize: {
    ...tr.localize,
    // eslint-disable-next-line no-unused-vars
    day: (n, options) => {
      // react-day-picker calls localize.day with { width: "short" } for headers.
      if (options?.width === "short") return TR_SHORT_DAYS[n === 0 ? 6 : n - 1];
      return tr.localize.day(n, options);
    },
  },
};

export function DatePicker({
  value,
  onChange,
  placeholder = "Tarih seçin",
  minDate,
  maxDate,
  disabled,
  className = "",
  "data-testid": testid,
  required,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const dateObj = value && typeof value === "string" ? parseISO(value) : null;
  const isValidDate = dateObj && isValid(dateObj);

  const iso = (d) => (d ? format(d, "yyyy-MM-dd") : "");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm text-left transition-colors",
            "hover:border-brand-orange/60 focus:outline-none focus:ring-2 focus:ring-brand-orange/40",
            "disabled:cursor-not-allowed disabled:opacity-60",
            !isValidDate && "text-slate-400",
            className
          )}
          {...rest}
        >
          <span className="flex items-center gap-2">
            <CalendarIcon size={14} className="text-brand-orange" />
            {isValidDate ? format(dateObj, "d MMMM yyyy · EEEE", { locale: tr }) : placeholder}
          </span>
          {isValidDate && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange?.(""); }}
              className="text-slate-400 hover:text-rose-600"
              aria-label="Tarihi temizle"
            >
              <X size={14} />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white" align="start" data-testid={testid ? `${testid}-popover` : undefined}>
        <Calendar
          mode="single"
          locale={trShort}
          weekStartsOn={1}
          captionLayout="dropdown-buttons"
          fromYear={new Date().getFullYear() - 2}
          toYear={new Date().getFullYear() + 5}
          classNames={{
            caption_dropdowns: "flex gap-1 justify-center",
            caption_label: "hidden",
            vhidden: "sr-only",
            nav: "hidden",
            dropdown: "rounded-md border border-slate-200 bg-white text-sm px-2 py-1 hover:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/40",
            dropdown_month: "text-brand-navy",
            dropdown_year: "text-brand-navy",
            head_cell: "text-slate-500 rounded-md w-9 font-semibold text-[11px] uppercase tracking-widest",
            day: "h-9 w-9 p-0 font-normal rounded-md hover:bg-brand-orange/10 aria-selected:opacity-100",
            day_selected: "bg-brand-orange text-white hover:bg-brand-orange hover:text-white focus:bg-brand-orange focus:text-white",
            day_today: "border-2 border-brand-orange text-brand-orange font-semibold",
          }}
          selected={isValidDate ? dateObj : undefined}
          onSelect={(d) => {
            onChange?.(iso(d));
            if (d) setOpen(false);
          }}
          disabled={{
            before: minDate ? (typeof minDate === "string" ? parseISO(minDate) : minDate) : undefined,
            after: maxDate ? (typeof maxDate === "string" ? parseISO(maxDate) : maxDate) : undefined,
          }}
          initialFocus
        />
        <div className="p-2 border-t border-slate-100 flex justify-between items-center">
          <button
            type="button"
            className="text-xs text-slate-500 hover:text-brand-orange"
            onClick={() => {
              const today = format(new Date(), "yyyy-MM-dd");
              onChange?.(today);
              setOpen(false);
            }}
          >
            Bugün
          </button>
          {required && (
            <span className="text-[10px] text-slate-400">Bu alan zorunlu</span>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DatePicker;
